import { motion, useReducedMotion } from 'framer-motion'

interface GalleryImage {
  src: string
  alt: string
}

interface RoomGallerySectionProps {
  title: string
  text: string
  images: GalleryImage[]
}

export function RoomGallerySection({
  title,
  text,
  images,
}: RoomGallerySectionProps) {
  const shouldReduceMotion = useReducedMotion()

  const baseTransition = { duration: 0.6, ease: 'easeOut' as const }
  const fadeUp = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 28 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-80px' },
        transition: baseTransition,
      }

  const paragraphs = text
    .replace(/\\n/g, '\n')
    .split('\n')
    .filter((line) => line.trim() !== '')

  // Layout expects 4 images: one tall portrait on the left, three stacked on the right.
  const [feature, ...stack] = images
  const [top, middle, bottom] = stack

  return (
    <section
      className="bg-maia-warm px-4 py-[clamp(4.5rem,10vw,8rem)] sm:px-6 lg:px-8"
      aria-labelledby="gallery-heading"
    >
      <div className="mx-auto max-w-6xl">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-14">
          {/* Copy */}
          <motion.div
            {...fadeUp}
            className="lg:col-span-5"
          >
            <h2
              id="gallery-heading"
              className="font-display text-[clamp(2.75rem,9vw,5.5rem)] font-extrabold leading-[0.9] tracking-tighter text-ink"
            >
              {title}
            </h2>

            <div className="relative mt-4 h-1.5 w-[clamp(5rem,18vw,11rem)] rounded-full bg-maia"
            />

            <div className="mt-8 max-w-lg space-y-5 text-lg leading-relaxed text-muted sm:text-xl">
              {paragraphs.map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>
          </motion.div>

          {/* Gallery */}
          <motion.div
            {...fadeUp}
            className="lg:col-span-7"
          >
            <div className="grid h-[clamp(28rem,50vw,40rem)] grid-cols-2 gap-3 sm:gap-4">
              {/* Feature image — tall, spans full height */}
              <div className="row-span-3 overflow-hidden rounded-2xl bg-silver shadow-lg"
              >
                <img
                  src={feature?.src}
                  alt={feature?.alt}
                  className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  loading="lazy"
                />
              </div>

              {/* Stacked images */}
              <div className="overflow-hidden rounded-2xl bg-silver shadow-md"
              >
                <img
                  src={top?.src}
                  alt={top?.alt}
                  className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="overflow-hidden rounded-2xl bg-silver shadow-md"
              >
                <img
                  src={middle?.src}
                  alt={middle?.alt}
                  className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="overflow-hidden rounded-2xl bg-silver shadow-md"
              >
                <img
                  src={bottom?.src}
                  alt={bottom?.alt}
                  className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  loading="lazy"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
